/* @refresh reload */
import { createBrowserRouter } from 'react-router-dom';
import { lazyWithErrorHandling } from '@/utils/lazyWithErrorHandling';
import ErrorPage from '@/pages/ErrorPage';
import NotFoundPage from '@/pages/NotFoundPage';
import { RouteWithTracking } from '@/core/ui/RouteWithTracking';
import { ROUTES } from '@/core/routes/constants';
import { PublicLayout } from '@/components/layouts';

const Landing = lazyWithErrorHandling(() => import('@/features/home/Landing'));
const LandingPreview = lazyWithErrorHandling(() => import('@/features/home/LandingPreview'));
const Login = lazyWithErrorHandling(() => import('@/features/auth/components/Login'));
const PhoneLogin = lazyWithErrorHandling(() => import('@/features/auth/components/PhoneLogin'));
const WhatsAppLogin = lazyWithErrorHandling(() => import('@/features/auth/components/WhatsAppLogin'));
const EmailOTPLogin = lazyWithErrorHandling(() => import('@/features/auth/components/EmailOTPLogin'));
const Register = lazyWithErrorHandling(() => import('@/features/auth/components/Register'));
const ForgotPassword = lazyWithErrorHandling(() => import('@/features/auth/components/ForgotPassword'));
const ResetPassword = lazyWithErrorHandling(() => import('@/features/auth/components/ResetPassword'));
const ConfirmPasswordChange = lazyWithErrorHandling(() => import('@/features/auth/components/ConfirmPasswordChange'));
const ConfirmPasswordCreation = lazyWithErrorHandling(() => import('@/features/auth/components/ConfirmPasswordCreation'));
const AuthorizeEmailChange = lazyWithErrorHandling(() => import('@/features/auth/components/AuthorizeEmailChange'));
const ConfirmEmailChange = lazyWithErrorHandling(() => import('@/features/auth/components/ConfirmEmailChange'));
const EmailConfirmation = lazyWithErrorHandling(() => import('@/features/auth/components/EmailConfirmation'));
const OAuthCallback = lazyWithErrorHandling(() => import('@/features/auth/components/OAuthCallback'));
const Profile = lazyWithErrorHandling(() => import('@/features/user/Profile'));
const MyOrders = lazyWithErrorHandling(() => import('@/features/user/MyOrders'));
const MyOrderDetail = lazyWithErrorHandling(() => import('@/features/user/MyOrderDetail'));
const AdminLayout = lazyWithErrorHandling(() => import('@/features/admin/AdminLayout'));
const AdminDashboard = lazyWithErrorHandling(() => import('@/features/admin/AdminDashboard'));
const UsersList = lazyWithErrorHandling(() => import('@/features/admin/Users/UsersList'));
const UserDetail = lazyWithErrorHandling(() => import('@/features/admin/Users/UserDetail'));
const RolesList = lazyWithErrorHandling(() => import('@/features/admin/Roles/RolesList'));
const RoleDetail = lazyWithErrorHandling(() => import('@/features/admin/Roles/RoleDetail'));
const RoleCreate = lazyWithErrorHandling(() => import('@/features/admin/Roles/RoleCreate'));
const AssignRoles = lazyWithErrorHandling(() => import('@/features/admin/Roles/AssignRoles'));
const AssignUserRoles = lazyWithErrorHandling(() => import('@/features/admin/Roles/AssignUserRoles'));
const ModulesList = lazyWithErrorHandling(() => import('@/features/admin/Modules/ModulesList'));
const MetaTagsList = lazyWithErrorHandling(() => import('@/features/admin/MetaTags/MetaTagsList'));
const MetaTagDetail = lazyWithErrorHandling(() => import('@/features/admin/MetaTags/MetaTagDetail'));

// Bots - BotDetail handles both create and edit modes
const BotsList = lazyWithErrorHandling(() => import('@/features/admin/Bots/BotsList'));
const BotDetail = lazyWithErrorHandling(() => import('@/features/admin/Bots/BotDetail'));
const BotsConfig = lazyWithErrorHandling(() => import('@/features/admin/Bots/BotsConfig'));

// Knowledge Bases
const KnowledgeBasesList = lazyWithErrorHandling(() => import('@/features/admin/KnowledgeBases/KnowledgeBasesList'));
const KnowledgeBaseDetail = lazyWithErrorHandling(() => import('@/features/admin/KnowledgeBases/KnowledgeBaseDetail'));
const KnowledgeBasesConfig = lazyWithErrorHandling(() => import('@/features/admin/KnowledgeBases/KnowledgeBasesConfig'));

// Knowledge Graph
const KnowledgeGraphAdmin = lazyWithErrorHandling(() => import('@/features/admin/KnowledgeGraph/KnowledgeGraphAdmin'));
const KnowledgeGraphExplorer = lazyWithErrorHandling(() => import('@/features/admin/KnowledgeGraph/KnowledgeGraphExplorer'));
const KnowledgeGraphPlanBuilder = lazyWithErrorHandling(() => import('@/features/admin/KnowledgeGraph/PlanBuilder'));

// Marketing
const MarketingDashboard = lazyWithErrorHandling(() => import('@/features/admin/Marketing/MarketingDashboard'));

// System (Configuration)
const SystemDashboard = lazyWithErrorHandling(() => import('@/features/admin/System/SystemDashboard'));
// Tracking
const TrackingDashboard = lazyWithErrorHandling(() => import('@/features/admin/Tracking/TrackingDashboard'));
// Monitoring
const MonitoringDashboard = lazyWithErrorHandling(() => import('@/features/admin/Monitoring/MonitoringDashboard'));
// Queues (Bull Board)
const QueuesPage = lazyWithErrorHandling(() => import('@/features/admin/Queues/QueuesPage'));
// Logs
const LogsDashboard = lazyWithErrorHandling(() => import('@/features/admin/Logs/LogsDashboard'));

// Support
const SupportInbox = lazyWithErrorHandling(() => import('@/features/admin/Support/SupportInbox'));
const SupportConversation = lazyWithErrorHandling(() => import('@/features/admin/Support/SupportConversation'));

// Inquiries
const InquiryInbox = lazyWithErrorHandling(() => import('@/features/admin/Inquiries/InquiryInbox'));

// Social Media
const SocialMediaContent = lazyWithErrorHandling(() => import('@/features/admin/SocialMedia/SocialMediaContent'));


// Corporate Brain
const CorporateBrain = lazyWithErrorHandling(() => import('@/features/admin/CorporateBrain/CorporateBrainPage'));
const CorporateDocs = lazyWithErrorHandling(() => import('@/features/admin/CorporateBrain/DocsExplorer'));

// My Plan (Supplement Calendar)
const MyPlan = lazyWithErrorHandling(() => import('@/features/user/MyPlan/MyPlanPage'));

// Chat
const Chat = lazyWithErrorHandling(() => import('@/features/chat/Chat'));
const TermsPage = lazyWithErrorHandling(() => import('@/pages/TermsPage'));
const ContactPage = lazyWithErrorHandling(() => import('@/pages/ContactPage'));

// Blog (Public)
const Blog = lazyWithErrorHandling(() => import('@/features/blog/Blog'));
const BlogPost = lazyWithErrorHandling(() => import('@/features/blog/BlogPost'));

// Blog (Admin)
const BlogConfiguration = lazyWithErrorHandling(() => import('@/features/admin/Blog/BlogConfiguration'));
const BlogTopics = lazyWithErrorHandling(() => import('@/features/admin/Blog/BlogTopics'));
const BlogArticles = lazyWithErrorHandling(() => import('@/features/admin/Blog/BlogArticles'));
const BlogArticleEditor = lazyWithErrorHandling(() => import('@/features/admin/Blog/BlogArticleEditor'));
const BlogGenerateArticle = lazyWithErrorHandling(() => import('@/features/admin/Blog/BlogGenerateArticle'));
const BlogPresets = lazyWithErrorHandling(() => import('@/features/admin/Blog/BlogPresets'));
const BlogIntegrations = lazyWithErrorHandling(() => import('@/features/admin/Blog/BlogIntegrations'));
const BlogAuditPolicies = lazyWithErrorHandling(() => import('@/features/admin/Blog/BlogAuditPolicies'));

// Products Admin
const ProductsList = lazyWithErrorHandling(() => import('@/features/admin/Products/ProductsList'));
const ProductDetail = lazyWithErrorHandling(() => import('@/features/admin/Products/ProductDetail'));
const ProductGroupsList = lazyWithErrorHandling(() => import('@/features/admin/Products/ProductGroupsList'));
const ProductGroupDetail = lazyWithErrorHandling(() => import('@/features/admin/Products/ProductGroupDetail'));

// Stores Admin (Shopify)
const StoresList = lazyWithErrorHandling(() => import('@/features/admin/Stores/StoresList'));
const StoreDetail = lazyWithErrorHandling(() => import('@/features/admin/Stores/StoreDetail'));

// Digital Assets Admin
const DigitalAssetsList = lazyWithErrorHandling(() => import('@/features/admin/DigitalAssets/DigitalAssetsList'));
const DigitalAssetDetail = lazyWithErrorHandling(() => import('@/features/admin/DigitalAssets/DigitalAssetDetail'));

// Ecommerce Settings & Analytics
const EcommerceSettings = lazyWithErrorHandling(() => import('@/features/admin/Ecommerce/EcommerceSettings'));
const EcommerceAnalytics = lazyWithErrorHandling(() => import('@/features/admin/Ecommerce/EcommerceAnalytics'));
const ProductAnalyticsPage = lazyWithErrorHandling(() => import('@/features/admin/Ecommerce/ProductAnalyticsPage'));
const ObservabilityDashboard = lazyWithErrorHandling(() => import('@/features/admin/Ecommerce/ObservabilityDashboard'));

// Product Planner
const ProductPlannerDashboard = lazyWithErrorHandling(() => import('@/features/admin/ProductPlanner/ProductPlannerDashboard'));
const ProductPlannerProductsList = lazyWithErrorHandling(() => import('@/features/admin/ProductPlanner/ProductsList'));
const ProductPlannerProductDetail = lazyWithErrorHandling(() => import('@/features/admin/ProductPlanner/ProductDetail'));
const ProductPlannerProductCreate = lazyWithErrorHandling(() => import('@/features/admin/ProductPlanner/ProductCreate'));
const ProductPlannerBenefitsList = lazyWithErrorHandling(() => import('@/features/admin/ProductPlanner/BenefitsList'));
const ProductPlannerIngredientsList = lazyWithErrorHandling(() => import('@/features/admin/ProductPlanner/IngredientsList'));
const ProductPlannerCatalogView = lazyWithErrorHandling(() => import('@/features/admin/ProductPlanner/CatalogView'));

// Storefront
const StorefrontLayout = lazyWithErrorHandling(() => import('@/features/storefront/layouts/StorefrontLayout'));
const ShopRedirect = lazyWithErrorHandling(() => import('@/features/storefront/pages/ShopRedirect'));
const ProductsPage = lazyWithErrorHandling(() => import('@/features/storefront/pages/ProductsPage'));
const ProductDetailPage = lazyWithErrorHandling(() => import('@/features/storefront/pages/ProductDetailPage'));
const ProductLandingPage = lazyWithErrorHandling(() => import('@/features/storefront/pages/ProductLandingPage'));
const CartPage = lazyWithErrorHandling(() => import('@/features/storefront/pages/CartPage'));
const NXT48LandingPreview = lazyWithErrorHandling(() => import('@/features/storefront/pages/NXT48LandingPreview'));

// Orders Admin
const OrdersList = lazyWithErrorHandling(() => import('@/features/admin/Orders/OrdersList'));
const OrderKanban = lazyWithErrorHandling(() => import('@/features/admin/Orders/OrderKanban'));
const OrderDetail = lazyWithErrorHandling(() => import('@/features/admin/Orders/OrderDetail'));

// NXT Level Ebooks
const NXTLevelLanding = lazyWithErrorHandling(() => import('@/features/ebooks/NXTLevelLanding'));

// Public Pages
const AddressCorrection = lazyWithErrorHandling(() => import('@/pages/public/AddressCorrection'));

export const router = createBrowserRouter([
  // Public routes with MainNavigation
  {
    element: (
      <RouteWithTracking>
        <PublicLayout />
      </RouteWithTracking>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        path: ROUTES.HOME,
        element: <Landing />,
      },
      {
        path: ROUTES.LANDING_PREVIEW,
        element: <LandingPreview />,
      },
      {
        path: ROUTES.PROFILE,
        element: <Profile />,
      },
      {
        path: ROUTES.MY_ORDERS,
        element: <MyOrders />,
      },
      {
        path: '/my-orders/:id',
        element: <MyOrderDetail />,
      },
      {
        path: ROUTES.MY_PLAN,
        element: <MyPlan />,
      },
      {
        path: ROUTES.TERMS,
        element: <TermsPage />,
      },
      {
        path: ROUTES.CONTACT,
        element: <ContactPage />,
      },
      {
        path: ROUTES.CHAT,
        element: <Chat />,
      },
      {
        path: `${ROUTES.CHAT}/:botId`,
        element: <Chat />,
      },
      {
        path: ROUTES.PLAN_BUILDER,
        element: <KnowledgeGraphPlanBuilder />,
      },
      {
        path: ROUTES.BLOG,
        element: <Blog />,
      },
      {
        path: `${ROUTES.BLOG}/:slug`,
        element: <BlogPost />,
      },
      {
        path: ROUTES.NXT_LEVEL,
        element: <NXTLevelLanding />,
      },
    ],
  },
  // Storefront redirect - /shop → /shop/:userPreferredStore
  {
    path: '/shop',
    element: (
      <RouteWithTracking>
        <ShopRedirect />
      </RouteWithTracking>
    ),
  },
  // Storefront routes - with storeSlug
  {
    path: '/shop/:storeSlug',
    element: (
      <RouteWithTracking>
        <StorefrontLayout />
      </RouteWithTracking>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: <ProductsPage />,
      },
      {
        path: 'products',
        element: <ProductsPage />,
      },
      {
        path: 'products/:handle',
        element: <ProductDetailPage />,
      },
      {
        path: 'products/:handle/landing',
        element: <ProductLandingPage />,
      },
      {
        path: 'cart',
        element: <CartPage />,
      },
    ],
  },
  // Auth routes (without MainNavigation - they have their own layout)
  {
    path: ROUTES.LOGIN,
    element: (
      <RouteWithTracking>
        <Login />
      </RouteWithTracking>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: ROUTES.PHONE_LOGIN,
    element: (
      <RouteWithTracking>
        <PhoneLogin />
      </RouteWithTracking>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: ROUTES.WHATSAPP_LOGIN,
    element: (
      <RouteWithTracking>
        <WhatsAppLogin />
      </RouteWithTracking>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: ROUTES.EMAIL_OTP_LOGIN,
    element: (
      <RouteWithTracking>
        <EmailOTPLogin />
      </RouteWithTracking>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: ROUTES.REGISTER,
    element: (
      <RouteWithTracking>
        <Register />
      </RouteWithTracking>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: ROUTES.FORGOT_PASSWORD,
    element: (
      <RouteWithTracking>
        <ForgotPassword />
      </RouteWithTracking>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: ROUTES.RESET_PASSWORD,
    element: (
      <RouteWithTracking>
        <ResetPassword />
      </RouteWithTracking>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: ROUTES.CONFIRM_PASSWORD_CHANGE,
    element: (
      <RouteWithTracking>
        <ConfirmPasswordChange />
      </RouteWithTracking>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: ROUTES.CONFIRM_PASSWORD_CREATION,
    element: (
      <RouteWithTracking>
        <ConfirmPasswordCreation />
      </RouteWithTracking>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: ROUTES.AUTHORIZE_EMAIL_CHANGE,
    element: (
      <RouteWithTracking>
        <AuthorizeEmailChange />
      </RouteWithTracking>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: ROUTES.CONFIRM_EMAIL_CHANGE,
    element: (
      <RouteWithTracking>
        <ConfirmEmailChange />
      </RouteWithTracking>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: ROUTES.EMAIL_CONFIRMATION,
    element: (
      <RouteWithTracking>
        <EmailConfirmation />
      </RouteWithTracking>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: ROUTES.OAUTH_CALLBACK,
    element: (
      <RouteWithTracking>
        <OAuthCallback />
      </RouteWithTracking>
    ),
    errorElement: <ErrorPage />,
  },
  // Admin routes (with AdminLayout)
  {
    path: ROUTES.ADMIN.ROOT,
    element: (
      <RouteWithTracking>
        <AdminLayout />
      </RouteWithTracking>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: <AdminDashboard />,
      },
      {
        path: 'users',
        element: <UsersList />,
      },
      {
        path: 'users/:id',
        element: <UserDetail />,
      },
      {
        path: 'roles',
        element: <RolesList />,
      },
      {
        path: 'roles/new',
        element: <RoleCreate />,
      },
      {
        path: 'roles/:id',
        element: <RoleDetail />,
      },
      {
        path: 'roles/:id/assign',
        element: <AssignRoles />,
      },
      {
        path: 'roles/assign-users',
        element: <AssignUserRoles />,
      },
      {
        path: 'modules',
        element: <ModulesList />,
      },
      {
        path: 'meta-tags',
        element: <MetaTagsList />,
      },
      {
        path: 'meta-tags/:id',
        element: <MetaTagDetail />,
      },
      // Bots - channels, conversations, tools, documents are tabs within BotDetail
      {
        path: 'bots',
        element: <BotsList />,
      },
      {
        path: 'bots/config',
        element: <BotsConfig />,
      },
      {
        path: 'bots/new',
        element: <BotDetail />,
      },
      {
        path: 'bots/:botId',
        element: <BotDetail />,
      },
      // Knowledge Bases
      {
        path: 'knowledge-bases',
        element: <KnowledgeBasesList />,
      },
      {
        path: 'knowledge-bases/config',
        element: <KnowledgeBasesConfig />,
      },
      {
        path: 'knowledge-bases/new',
        element: <KnowledgeBaseDetail />,
      },
      {
        path: 'knowledge-bases/:kbId',
        element: <KnowledgeBaseDetail />,
      },
      // Knowledge Graph
      {
        path: 'knowledge-graph',
        element: <KnowledgeGraphAdmin />,
      },
      {
        path: 'knowledge-graph/explorer',
        element: <KnowledgeGraphExplorer />,
      },
      // Marketing Analytics
      {
        path: 'marketing',
        element: <MarketingDashboard />,
      },
      // Tracking
      {
        path: 'tracking',
        element: <TrackingDashboard />,
      },
      // Monitoring
      {
        path: 'monitoring',
        element: <MonitoringDashboard />,
      },
      // Queues (Bull Board)
      {
        path: 'queues',
        element: <QueuesPage />,
      },
      // System (Configuration)
      {
        path: 'system',
        element: <SystemDashboard />,
      },
      // Logs
      {
        path: 'logs',
        element: <LogsDashboard />,
      },
      // Support
      {
        path: 'support',
        element: <SupportInbox />,
      },
      {
        path: 'support/:conversationId',
        element: <SupportConversation />,
      },
      // Inquiries
      {
        path: 'inquiries',
        element: <InquiryInbox />,
      },
      // Social Media
      {
        path: 'social-media',
        element: <SocialMediaContent />,
      },
      // Blog Admin
      {
        path: 'blog',
        element: <BlogConfiguration />,
      },
      {
        path: 'blog/topics',
        element: <BlogTopics />,
      },
      {
        path: 'blog/articles',
        element: <BlogArticles />,
      },
      {
        path: 'blog/articles/new',
        element: <BlogArticleEditor />,
      },
      {
        path: 'blog/articles/:id/edit',
        element: <BlogArticleEditor />,
      },
      {
        path: 'blog/articles/generate/:topicId',
        element: <BlogGenerateArticle />,
      },
      {
        path: 'blog/presets',
        element: <BlogPresets />,
      },
      {
        path: 'blog/integrations',
        element: <BlogIntegrations />,
      },
      {
        path: 'blog/audit-policies',
        element: <BlogAuditPolicies />,
      },
      // Products Admin
      {
        path: 'products',
        element: <ProductGroupsList />,  // New: Show groups by default
      },
      {
        path: 'products/list',
        element: <ProductsList />,  // Old flat list (for backwards compatibility)
      },
      {
        path: 'products/groups/:groupId',
        element: <ProductGroupDetail />,
      },
      {
        path: 'products/:id',
        element: <ProductDetail />,
      },
      // Stores Admin (Shopify)
      {
        path: 'stores',
        element: <StoresList />,
      },
      {
        path: 'stores/:id',
        element: <StoreDetail />,
      },
      // Digital Assets Admin
      {
        path: 'digital-assets',
        element: <DigitalAssetsList />,
      },
      {
        path: 'digital-assets/new',
        element: <DigitalAssetDetail />,
      },
      {
        path: 'digital-assets/:id',
        element: <DigitalAssetDetail />,
      },
      // Ecommerce Settings
      {
        path: 'ecommerce/settings',
        element: <EcommerceSettings />,
      },
      // Ecommerce Analytics
      {
        path: 'ecommerce/analytics',
        element: <EcommerceAnalytics />,
      },
      {
        path: 'ecommerce/analytics/products/:productId',
        element: <ProductAnalyticsPage />,
      },
      {
        path: 'ecommerce/observability',
        element: <ObservabilityDashboard />,
      },
      // Orders
      {
        path: 'orders',
        element: <OrdersList />,
      },
      {
        path: 'orders/kanban',
        element: <OrderKanban />,
      },
      {
        path: 'orders/:id',
        element: <OrderDetail />,
      },
      // Corporate Brain
      {
        path: 'corporate-brain',
        element: <CorporateBrain />,
      },
      {
        path: 'corporate-docs',
        element: <CorporateDocs />,
      },
      // Product Planner
      {
        path: 'product-planner',
        element: <ProductPlannerDashboard />,
      },
      {
        path: 'product-planner/products',
        element: <ProductPlannerProductsList />,
      },
      {
        path: 'product-planner/products/create',
        element: <ProductPlannerProductCreate />,
      },
      {
        path: 'product-planner/products/:id',
        element: <ProductPlannerProductDetail />,
      },
      {
        path: 'product-planner/benefits',
        element: <ProductPlannerBenefitsList />,
      },
      {
        path: 'product-planner/ingredients',
        element: <ProductPlannerIngredientsList />,
      },
      {
        path: 'product-planner/catalog',
        element: <ProductPlannerCatalogView />,
      },
    ],
  },
  // NXT48 Landing Preview (dev only)
  {
    path: '/nxt48-preview',
    element: (
      <RouteWithTracking>
        <NXT48LandingPreview />
      </RouteWithTracking>
    ),
    errorElement: <ErrorPage />,
  },
  // Public address correction (no auth required)
  {
    path: '/address-correction/:token',
    element: (
      <RouteWithTracking>
        <AddressCorrection />
      </RouteWithTracking>
    ),
    errorElement: <ErrorPage />,
  },
  {
    path: '*',
    element: (
      <RouteWithTracking>
        <NotFoundPage />
      </RouteWithTracking>
    ),
  },
]);
